import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Shield, CheckCircle2, XCircle, AlertTriangle, Play, Users, ChevronDown, ChevronRight, History, Info } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import SectionHelp from '@/components/help/SectionHelp';
import VerticalExampleBox from '@/components/help/VerticalExampleBox';
import EmptyStateGuide from '@/components/help/EmptyStateGuide';
import FieldHelp from '@/components/help/FieldHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import helpContent from '@/help/helpContent';
import sensitiveActions from '@/help/sensitiveActions';

const STATUS_LABELS = {
  draft: 'Borrador', preflight_required: 'Preflight requerido', preflight_passed: 'Preflight OK',
  ready_for_delivery_future: 'Listo (futuro)', cancelled: 'Cancelado', archived: 'Archivado',
};
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700', preflight_required: 'bg-amber-100 text-amber-700',
  preflight_passed: 'bg-emerald-100 text-emerald-700', ready_for_delivery_future: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700', archived: 'bg-gray-200 text-gray-500',
};
const PREFLIGHT_COLORS = { not_run: 'text-gray-400', passed: 'text-emerald-600', failed: 'text-red-600', warnings: 'text-amber-600' };

// Revisión de seguridad (preflight): resultado en palabras del negocio
const RUN_STATUS_LABELS = { passed: 'Todo en orden', failed: 'Con errores por corregir', warnings: 'Con advertencias' };
const RUN_STATUS_COLORS = { passed: 'text-emerald-600', failed: 'text-red-600', warnings: 'text-amber-600' };

// Actividad de la programación: eventos que guarda el sistema, traducidos
const EVENT_LABELS = {
  schedule_created: 'Se creó la programación',
  schedule_updated: 'Se editó la programación',
  schedule_cancelled: 'Se canceló la programación',
  schedule_archived: 'Se archivó la programación',
  audience_snapshot_created: 'Se tomó una copia de la lista de contactos',
  preflight_run_passed: 'Revisión de seguridad: todo en orden',
  preflight_run_failed: 'Revisión de seguridad: con errores',
  preflight_run_warnings: 'Revisión de seguridad: con advertencias',
  marked_ready: 'Se marcó como lista para enviar',
};

// Copia de la lista (snapshot): estado, permisos y motivos, sin jerga
const SNAPSHOT_STATUS_LABELS = { created: 'Vigente', stale: 'Desactualizada', archived: 'Archivada' };
const CONSENT_LABELS = { opted_in: 'Aceptó recibir', opted_out: 'Pidió no recibir', unknown: 'Sin permiso registrado' };
const ELIGIBILITY_LABELS = { eligible: 'Puede recibir', blocked: 'No puede recibir', warning: 'Requiere revisión' };
const ELIGIBILITY_COLORS = { eligible: 'text-emerald-600', blocked: 'text-red-600', warning: 'text-amber-600' };
const EXCLUSION_LABELS = {
  opted_out: 'La persona pidió no recibir mensajes',
  consent_unknown_blocked_by_policy: 'No tiene permiso registrado y tu regla lo deja fuera',
  consent_unknown: 'No tiene permiso registrado; se incluye bajo tu responsabilidad',
  consent_status_unrecognized: 'El permiso registrado no se reconoce',
};
const UNKNOWN_POLICY_LABELS = {
  block: 'quedan fuera del envío',
  warning: 'se incluyen, pero marcados para revisión',
  allow: 'se incluyen sin restricción',
};

const MEMBER_FILTERS = [
  { key: '', label: 'Todos' },
  { key: 'blocked', label: 'No pueden recibir' },
  { key: 'warning', label: 'Requieren revisión' },
  { key: 'eligible', label: 'Pueden recibir' },
];

const RUNS_PAGE_SIZE = 10;
const LOGS_PAGE_SIZE = 20;
const MEMBERS_PAGE_SIZE = 25;
const SNAPSHOT_STALE_DAYS = 7;

function Badge({ status, map }) {
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>{STATUS_LABELS[status] || status}</span>;
}

const fmtDate = (v) => (v ? new Date(v).toLocaleString('es-MX') : 'Sin fecha');
const daysSince = (v) => {
  if (!v) return null;
  const t = new Date(v).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
};
// "Snapshot created: 42 contacts" -> "42 contactos incluidos" (el backend guarda el texto en inglés)
const logExtra = (l) => {
  if (l.event_type !== 'audience_snapshot_created') return '';
  const m = /(\d+)/.exec(l.message || '');
  return m ? `${m[1]} contactos incluidos` : '';
};

export default function CampaignScheduler() {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [preflightResult, setPreflightResult] = useState(null);
  const [details, setDetails] = useState({});
  const [members, setMembers] = useState(null);
  const [form, setForm] = useState({ campaign_id: '', planned_channel: 'generic', planned_start_at: '', timezone: 'America/Mexico_City' });
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [sRes, cRes] = await Promise.all([
        api.get('/tenant/campaign-schedules'),
        api.get('/tenant/campaigns'),
      ]);
      setSchedules(sRes.data.items || []);
      setCampaigns(cRes.data.items || cRes.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Carga perezosa: el detalle de una programación se pide al abrir su fila, no al entrar a la pantalla
  const loadDetail = useCallback(async (id) => {
    setDetails(prev => ({ ...prev, [id]: { loading: true, runs: [], runsTotal: 0, runsPage: 1, logs: [], logsTotal: 0, logsPage: 1, logsAvailable: true } }));
    const [snapRes, runsRes, logsRes] = await Promise.allSettled([
      api.get(`/tenant/campaign-schedules/${id}/snapshot`),
      api.get(`/tenant/campaign-schedules/${id}/preflight-runs`, { params: { page: 1, page_size: RUNS_PAGE_SIZE } }),
      api.get(`/tenant/campaign-schedules/${id}/logs`, { params: { page: 1, page_size: LOGS_PAGE_SIZE } }),
    ]);

    const next = { loading: false, runsPage: 1, logsPage: 1 };

    if (snapRes.status === 'fulfilled') {
      next.snapshot = snapRes.value.data;
      next.snapshotError = '';
    } else {
      next.snapshot = null;
      next.snapshotError = snapRes.reason?.response?.status === 404
        ? ''
        : (formatApiError(snapRes.reason?.response?.data?.detail) || 'No pudimos cargar la copia de la lista.');
    }

    if (runsRes.status === 'fulfilled') {
      next.runs = runsRes.value.data.items || [];
      next.runsTotal = runsRes.value.data.total || 0;
      next.runsError = '';
    } else {
      next.runs = [];
      next.runsTotal = 0;
      next.runsError = formatApiError(runsRes.reason?.response?.data?.detail) || 'No pudimos cargar el historial de revisiones.';
    }

    if (logsRes.status === 'fulfilled') {
      next.logs = logsRes.value.data.items || [];
      next.logsTotal = logsRes.value.data.total || 0;
      next.logsAvailable = true;
      next.logsError = '';
    } else if (logsRes.reason?.response?.status === 403) {
      // El historial de actividad es una función del plan: si no está incluida, simplemente no se muestra
      next.logs = [];
      next.logsTotal = 0;
      next.logsAvailable = false;
      next.logsError = '';
    } else {
      next.logs = [];
      next.logsTotal = 0;
      next.logsAvailable = true;
      next.logsError = formatApiError(logsRes.reason?.response?.data?.detail) || 'No pudimos cargar la actividad.';
    }

    setDetails(prev => ({ ...prev, [id]: next }));
  }, []);

  useEffect(() => {
    if (expanded && !details[expanded]) loadDetail(expanded);
  }, [expanded, details, loadDetail]);

  const loadMoreRuns = async (id) => {
    const cur = details[id];
    if (!cur) return;
    const page = (cur.runsPage || 1) + 1;
    try {
      const r = await api.get(`/tenant/campaign-schedules/${id}/preflight-runs`, { params: { page, page_size: RUNS_PAGE_SIZE } });
      setDetails(prev => ({ ...prev, [id]: { ...prev[id], runs: [...(prev[id]?.runs || []), ...(r.data.items || [])], runsTotal: r.data.total || 0, runsPage: page, runsError: '' } }));
    } catch (e) {
      setDetails(prev => ({ ...prev, [id]: { ...prev[id], runsError: formatApiError(e.response?.data?.detail) || 'No pudimos cargar más revisiones.' } }));
    }
  };

  const loadMoreLogs = async (id) => {
    const cur = details[id];
    if (!cur) return;
    const page = (cur.logsPage || 1) + 1;
    try {
      const r = await api.get(`/tenant/campaign-schedules/${id}/logs`, { params: { page, page_size: LOGS_PAGE_SIZE } });
      setDetails(prev => ({ ...prev, [id]: { ...prev[id], logs: [...(prev[id]?.logs || []), ...(r.data.items || [])], logsTotal: r.data.total || 0, logsPage: page, logsError: '' } }));
    } catch (e) {
      setDetails(prev => ({ ...prev, [id]: { ...prev[id], logsError: formatApiError(e.response?.data?.detail) || 'No pudimos cargar más actividad.' } }));
    }
  };

  const fetchMembers = async (scheduleId, filter, page) => {
    setMembers(prev => (prev ? { ...prev, scheduleId, filter, page, loading: true, error: '' } : prev));
    try {
      const params = { page, page_size: MEMBERS_PAGE_SIZE };
      if (filter) params.eligibility_status = filter;
      const r = await api.get(`/tenant/campaign-schedules/${scheduleId}/snapshot/members`, { params });
      setMembers(prev => (prev && prev.scheduleId === scheduleId
        ? { ...prev, loading: false, items: r.data.items || [], total: r.data.total || 0 }
        : prev));
    } catch (e) {
      const msg = e.response?.status === 404
        ? 'Todavía no se ha tomado una copia de la lista de contactos para esta programación.'
        : (formatApiError(e.response?.data?.detail) || 'No pudimos cargar la lista de contactos.');
      setMembers(prev => (prev && prev.scheduleId === scheduleId
        ? { ...prev, loading: false, items: [], total: 0, error: msg }
        : prev));
    }
  };

  const openMembers = (scheduleId) => {
    setMembers({ scheduleId, filter: '', page: 1, items: [], total: 0, loading: true, error: '' });
    fetchMembers(scheduleId, '', 1);
  };

  const create = async () => {
    if (!form.campaign_id) { setError('Selecciona una campaña'); return; }
    setError('');
    try {
      const body = { ...form };
      if (body.planned_start_at) body.planned_start_at = new Date(body.planned_start_at).toISOString();
      await api.post('/tenant/campaign-schedules', body);
      setCreating(false);
      setForm({ campaign_id: '', planned_channel: 'generic', planned_start_at: '', timezone: 'America/Mexico_City' });
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error al crear'); }
  };

  const runAction = async (id, action) => {
    try {
      if (action === 'cancel') await api.patch(`/tenant/campaign-schedules/${id}/cancel`);
      else if (action === 'archive') await api.patch(`/tenant/campaign-schedules/${id}/archive`);
      else if (action === 'snapshot') await api.post(`/tenant/campaign-schedules/${id}/audience-snapshot`);
      else if (action === 'preflight') {
        const r = await api.post(`/tenant/campaign-schedules/${id}/preflight`);
        setPreflightResult(r.data);
      }
      else if (action === 'mark-ready') await api.post(`/tenant/campaign-schedules/${id}/mark-ready`);
      // El detalle abierto quedó viejo tras la acción: se vuelve a pedir si la fila sigue abierta
      setDetails(prev => { const n = { ...prev }; delete n[id]; return n; });
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || `Error en ${action}`); }
  };

  const campaignName = (id) => campaigns.find(c => c.id === id)?.name || id?.slice(0, 8);
  const membersSchedule = members ? schedules.find(s => s.id === members.scheduleId) : null;
  const membersPages = members ? Math.max(1, Math.ceil((members.total || 0) / MEMBERS_PAGE_SIZE)) : 1;

  if (loading) return <PantallaCargando data-testid="campaign-scheduler-page" />;

  return (
    <div className="space-y-4" data-testid="campaign-scheduler-page">
      <SectionHelp module="schedules" title={helpContent.schedules?.title || 'Programación'} description={helpContent.schedules?.description} whatItDoesNot={helpContent.schedules?.whatItDoesNot} />
      <VerticalExampleBox module="schedules" />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2"><Calendar className="w-4 h-4" /> Programación de Campañas</h2>
          <p className="text-sm text-[#64748B]">Define cuándo y bajo qué condiciones enviar, sin ejecutar envíos</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setCreating(!creating)} data-testid="create-schedule-btn"
            className="px-3 py-1.5 bg-[#F72585] text-white text-sm rounded-md hover:bg-[#D91A72] transition-colors">
            + Nueva Programación
          </button>
          <button onClick={() => navigate('/tenant/marketing/delivery')} data-testid="go-delivery-btn"
            className="px-3 py-1.5 border border-[#CBD5E1] text-sm rounded-md hover:bg-[#F1F5F9] transition-colors text-[#334155]">
            Ir a Entregas
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded" data-testid="schedule-error">{error}<button className="ml-2 underline" onClick={() => setError('')}>cerrar</button></div>}

      {creating && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 space-y-3" data-testid="create-schedule-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldHelp label="Campaña" help="Selecciona la campaña a programar." htmlFor="schedule-campaign">
              <select value={form.campaign_id} onChange={e => setForm(f => ({ ...f, campaign_id: e.target.value }))}
                data-testid="schedule-campaign-select" id="schedule-campaign"
                className="w-full border border-[#CBD5E1] rounded px-2 py-1.5 text-sm">
                <option value="">-- Seleccionar --</option>
                {campaigns.map(c => <option key={c.id} value={c.id}>{c.name} ({c.status})</option>)}
              </select>
            </FieldHelp>
            <FieldHelp label={helpContent.schedules.fields.planned_channel.label} help={helpContent.schedules.fields.planned_channel.help} htmlFor="schedule-channel">
              <select value={form.planned_channel} onChange={e => setForm(f => ({ ...f, planned_channel: e.target.value }))}
                data-testid="schedule-channel-select" id="schedule-channel"
                className="w-full border border-[#CBD5E1] rounded px-2 py-1.5 text-sm">
                {['generic', 'whatsapp_future', 'email_future', 'sms_future', 'social_future'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </FieldHelp>
            <FieldHelp label={helpContent.schedules.fields.planned_start_at.label} help={helpContent.schedules.fields.planned_start_at.help} htmlFor="schedule-start">
              <input type="datetime-local" value={form.planned_start_at} onChange={e => setForm(f => ({ ...f, planned_start_at: e.target.value }))}
                data-testid="schedule-start-input" id="schedule-start"
                className="w-full border border-[#CBD5E1] rounded px-2 py-1.5 text-sm" />
            </FieldHelp>
            <FieldHelp label="Timezone" help="Zona horaria para el envío programado." htmlFor="schedule-timezone">
              <input value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}
                data-testid="schedule-timezone-input" id="schedule-timezone"
                className="w-full border border-[#CBD5E1] rounded px-2 py-1.5 text-sm" />
            </FieldHelp>
          </div>
          <p className="text-xs text-[#94A3B8] italic">Tip: Asegúrate de vincular una audiencia a la campaña antes de programar (Campaign Builder → Audiencias vinculadas).</p>
          <div className="flex gap-2">
            <button onClick={create} data-testid="schedule-save-btn" className="px-3 py-1.5 bg-[#0F172A] text-white text-sm rounded hover:bg-[#1E293B]">Crear Schedule</button>
            <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-sm text-[#64748B] hover:text-[#0F172A]">Cancelar</button>
          </div>
        </div>
      )}

      {schedules.length === 0 && !creating && (
        <EmptyStateGuide module="schedules" message="No hay programaciones. Crea una para definir cuándo enviar tu campaña." ctaLabel="Nueva Programación" onCta={() => setCreating(true)} />
      )}

      <div className="space-y-2">
        {schedules.map(s => {
          // Sin entrada aún = la fila se acaba de abrir y el detalle todavía se está pidiendo
          const d = details[s.id] || { loading: true };
          const snapAge = daysSince(d.snapshot?.created_at);
          return (
            <div key={s.id} className="bg-white border border-[#E2E8F0] rounded-lg" data-testid={`schedule-row-${s.id}`}>
              <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-[#F8FAFC]"
                onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                <div className="flex items-center gap-3 min-w-0">
                  {expanded === s.id ? <ChevronDown className="w-4 h-4 text-[#94A3B8] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#94A3B8] shrink-0" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#0F172A] truncate">{campaignName(s.campaign_id)}</span>
                      <Badge status={s.status} map={STATUS_COLORS} />
                      <span className={`text-xs ${PREFLIGHT_COLORS[s.preflight_status] || ''}`}>
                        {s.preflight_status === 'passed' && <CheckCircle2 className="w-3 h-3 inline mr-0.5" />}
                        {s.preflight_status === 'failed' && <XCircle className="w-3 h-3 inline mr-0.5" />}
                        {s.preflight_status === 'warnings' && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
                        {s.preflight_status !== 'not_run' && s.preflight_status}
                      </span>
                    </div>
                    <div className="text-xs text-[#94A3B8] mt-0.5">
                      {s.planned_channel} | {s.planned_start_at ? new Date(s.planned_start_at).toLocaleString('es-MX') : 'Sin fecha'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!['cancelled', 'archived', 'ready_for_delivery_future'].includes(s.status) && (
                    <>
                      <SensitiveActionHelp action={sensitiveActions.schedule_snapshot} onConfirm={() => runAction(s.id, 'snapshot')}>
                        <button title="Crear Snapshot" data-testid={`snapshot-btn-${s.id}`}
                          className="p-1 rounded hover:bg-[#F1F5F9]"><Users className="w-3.5 h-3.5 text-[#64748B]" /></button>
                      </SensitiveActionHelp>
                      <button onClick={e => { e.stopPropagation(); runAction(s.id, 'preflight'); }}
                        title="Ejecutar Preflight" data-testid={`preflight-btn-${s.id}`}
                        className="p-1 rounded hover:bg-[#F1F5F9]"><Shield className="w-3.5 h-3.5 text-[#64748B]" /></button>
                    </>
                  )}
                  {s.status === 'preflight_passed' && (
                    <SensitiveActionHelp action={sensitiveActions.schedule_mark_ready} onConfirm={() => runAction(s.id, 'mark-ready')}>
                      <button title="Marcar Listo" data-testid={`mark-ready-btn-${s.id}`}
                        className="p-1 rounded hover:bg-emerald-50"><Play className="w-3.5 h-3.5 text-emerald-600" /></button>
                    </SensitiveActionHelp>
                  )}
                  {!['cancelled', 'archived'].includes(s.status) && (
                    <SensitiveActionHelp action={sensitiveActions.schedule_cancel} onConfirm={() => runAction(s.id, 'cancel')}>
                      <button title="Cancelar" data-testid={`cancel-btn-${s.id}`}
                        className="p-1 rounded hover:bg-red-50"><XCircle className="w-3.5 h-3.5 text-red-400" /></button>
                    </SensitiveActionHelp>
                  )}
                </div>
              </div>

              {expanded === s.id && (
                <div className="border-t border-[#E2E8F0] p-3 bg-[#F8FAFC] text-xs space-y-4" data-testid={`schedule-detail-${s.id}`}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div><span className="text-[#94A3B8]">Estimados:</span> <strong>{s.estimated_recipients}</strong></div>
                    <div><span className="text-[#94A3B8]">Elegibles:</span> <strong className="text-emerald-600">{s.eligible_recipients}</strong></div>
                    <div><span className="text-[#94A3B8]">Bloqueados:</span> <strong className="text-red-500">{s.blocked_recipients}</strong></div>
                    <div><span className="text-[#94A3B8]">Advertencia:</span> <strong className="text-amber-500">{s.warning_recipients}</strong></div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[#64748B]">
                    <div>Ambiente: {s.environment}</div>
                    <div>Zona: {s.timezone}</div>
                    <div className="flex items-center gap-1">
                      <span>Contactos sin permiso registrado: {UNKNOWN_POLICY_LABELS[s.consent_policy?.unknown] || 'se incluyen, pero marcados para revisión'}</span>
                      <HelpTooltip text="Regla que decide qué pasa con los contactos de los que no tienes un permiso registrado para escribirles. Quien pidió no recibir mensajes siempre queda fuera." />
                    </div>
                  </div>

                  {/* ── Copia de la lista de contactos ─────────────────────────── */}
                  <div className="bg-white border border-[#E2E8F0] rounded-md p-3 space-y-2">
                    <div className="flex items-center gap-1 font-medium text-[#0F172A]">
                      <Users className="w-3.5 h-3.5 text-[#64748B]" />
                      <span>Copia de la lista de contactos</span>
                      <HelpTooltip text="Es la foto de tu lista en el momento en que la tomaste: a quién se le puede escribir y a quién no. La campaña se envía con esta copia, no con la lista de hoy." />
                    </div>

                    {d.loading && <p className="text-[#94A3B8]">Cargando información...</p>}

                    {!d.loading && d.snapshotError && (
                      <p className="text-red-600" data-testid={`snapshot-error-${s.id}`}>{d.snapshotError}</p>
                    )}

                    {!d.loading && !d.snapshotError && !d.snapshot && (
                      <p className="text-[#94A3B8]">
                        Todavía no has tomado una copia de esta lista. Usa el botón de personas (arriba, en esta misma fila) para tomarla antes de revisar la campaña.
                      </p>
                    )}

                    {!d.loading && d.snapshot && (
                      <>
                        <p className="text-[#64748B]">
                          Tomada el <strong className="text-[#0F172A]">{fmtDate(d.snapshot.created_at)}</strong>
                          {d.snapshot.snapshot_status && (
                            <span className="text-[#94A3B8]"> · {SNAPSHOT_STATUS_LABELS[d.snapshot.snapshot_status] || d.snapshot.snapshot_status}</span>
                          )}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Aceptó recibir: {d.snapshot.consent_summary?.opted_in || 0}</span>
                          <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700">Pidió no recibir: {d.snapshot.consent_summary?.opted_out || 0}</span>
                          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Sin permiso registrado: {d.snapshot.consent_summary?.unknown || 0}</span>
                        </div>
                        {(d.snapshot.snapshot_status === 'stale' || (snapAge !== null && snapAge >= SNAPSHOT_STALE_DAYS)) && (
                          <p className="flex items-start gap-1 text-amber-700 bg-amber-50 px-2 py-1 rounded" data-testid={`snapshot-stale-${s.id}`}>
                            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                            <span>Esta copia ya tiene {snapAge} {snapAge === 1 ? 'día' : 'días'}. Vuelve a tomarla para trabajar con datos frescos antes de marcar la campaña como lista.</span>
                          </p>
                        )}
                        <button onClick={() => openMembers(s.id)} data-testid={`view-members-btn-${s.id}`}
                          className="text-[#F72585] hover:text-[#D91A72] underline underline-offset-2">
                          Ver contacto por contacto por qué se incluyó o se excluyó
                        </button>
                      </>
                    )}
                  </div>

                  {/* ── Historial de revisiones de seguridad ───────────────────── */}
                  <div className="bg-white border border-[#E2E8F0] rounded-md p-3 space-y-2">
                    <div className="flex items-center gap-1 font-medium text-[#0F172A]">
                      <Shield className="w-3.5 h-3.5 text-[#64748B]" />
                      <span>Historial de revisiones de seguridad</span>
                      <HelpTooltip text="Cada vez que corres la revisión de seguridad guardamos el resultado. Haz clic en cualquier revisión para volver a ver el detalle completo de lo que se revisó." />
                    </div>

                    {d.loading && <p className="text-[#94A3B8]">Cargando revisiones...</p>}
                    {!d.loading && d.runsError && <p className="text-red-600" data-testid={`runs-error-${s.id}`}>{d.runsError}</p>}
                    {!d.loading && !d.runsError && (d.runs || []).length === 0 && (
                      <p className="text-[#94A3B8]">Sin revisiones todavía. Usa el botón del escudo (arriba, en esta misma fila) para hacer la primera.</p>
                    )}

                    {!d.loading && (d.runs || []).length > 0 && (
                      <div className="divide-y divide-[#F1F5F9]" data-testid={`runs-list-${s.id}`}>
                        {d.runs.map(run => (
                          <button key={run.id} onClick={() => setPreflightResult(run)} data-testid={`run-row-${run.id}`}
                            className="w-full flex items-center justify-between gap-2 py-1.5 text-left hover:bg-[#F8FAFC] rounded px-1">
                            <span className="flex items-center gap-1.5 min-w-0">
                              {run.status === 'passed' && <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />}
                              {run.status === 'failed' && <XCircle className="w-3 h-3 text-red-500 shrink-0" />}
                              {run.status === 'warnings' && <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />}
                              <span className={`font-medium ${RUN_STATUS_COLORS[run.status] || 'text-[#334155]'}`}>{RUN_STATUS_LABELS[run.status] || run.status}</span>
                              {(run.blocking_errors?.length || 0) > 0 && (
                                <span className="text-red-600">· {run.blocking_errors.length} {run.blocking_errors.length === 1 ? 'error por corregir' : 'errores por corregir'}</span>
                              )}
                              {(run.warnings?.length || 0) > 0 && (
                                <span className="text-amber-600">· {run.warnings.length} {run.warnings.length === 1 ? 'advertencia' : 'advertencias'}</span>
                              )}
                            </span>
                            <span className="text-[#94A3B8] shrink-0">{fmtDate(run.created_at)}</span>
                          </button>
                        ))}
                        {(d.runs.length < (d.runsTotal || 0)) && (
                          <button onClick={() => loadMoreRuns(s.id)} data-testid={`runs-more-btn-${s.id}`}
                            className="mt-1 text-[#64748B] hover:text-[#0F172A] underline underline-offset-2">
                            Ver revisiones anteriores ({d.runsTotal - d.runs.length} más)
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Actividad (solo si el plan la incluye) ─────────────────── */}
                  {(d.loading || d.logsAvailable !== false) && (
                    <div className="bg-white border border-[#E2E8F0] rounded-md p-3 space-y-2">
                      <div className="flex items-center gap-1 font-medium text-[#0F172A]">
                        <History className="w-3.5 h-3.5 text-[#64748B]" />
                        <span>Actividad de esta programación</span>
                        <HelpTooltip text="Todo lo que ha pasado con esta programación: quién la creó, cuándo se tomó la copia de la lista, cada revisión y cuándo se marcó como lista." />
                      </div>

                      {d.loading && <p className="text-[#94A3B8]">Cargando actividad...</p>}
                      {!d.loading && d.logsError && <p className="text-red-600" data-testid={`logs-error-${s.id}`}>{d.logsError}</p>}
                      {!d.loading && !d.logsError && (d.logs || []).length === 0 && (
                        <p className="text-[#94A3B8]">Sin registros todavía.</p>
                      )}

                      {!d.loading && (d.logs || []).length > 0 && (
                        <div className="space-y-1.5" data-testid={`logs-list-${s.id}`}>
                          {d.logs.map(l => (
                            <div key={l.id} className="flex items-start gap-2">
                              <Clock className="w-3 h-3 text-[#CBD5E1] mt-0.5 shrink-0" />
                              <div className="min-w-0">
                                <div className="text-[#334155]">{EVENT_LABELS[l.event_type] || 'Cambio en la programación'}</div>
                                {logExtra(l) && <div className="text-[#94A3B8]">{logExtra(l)}</div>}
                                <div className="text-[#94A3B8]">{fmtDate(l.created_at)}</div>
                              </div>
                            </div>
                          ))}
                          {(d.logs.length < (d.logsTotal || 0)) && (
                            <button onClick={() => loadMoreLogs(s.id)} data-testid={`logs-more-btn-${s.id}`}
                              className="text-[#64748B] hover:text-[#0F172A] underline underline-offset-2">
                              Ver más actividad ({d.logsTotal - d.logs.length} registros más)
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {preflightResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="preflight-modal">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#0F172A]">Resultado de la revisión de seguridad</h3>
              <button onClick={() => setPreflightResult(null)} data-testid="preflight-close-btn" className="text-[#94A3B8] hover:text-[#0F172A]">x</button>
            </div>
            {preflightResult.created_at && (
              <p className="text-xs text-[#94A3B8] mb-2">Revisión hecha el {fmtDate(preflightResult.created_at)}</p>
            )}
            <div className={`text-sm font-medium mb-2 ${preflightResult.status === 'passed' ? 'text-emerald-600' : preflightResult.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>
              Resultado: {RUN_STATUS_LABELS[preflightResult.status] || preflightResult.status}
            </div>
            {preflightResult.blocking_errors?.length > 0 && (
              <div className="mb-2">
                <p className="text-xs font-medium text-red-700 mb-1">Debes corregir esto antes de continuar:</p>
                {preflightResult.blocking_errors.map((e, i) => <div key={i} className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded mb-1">{e}</div>)}
              </div>
            )}
            {preflightResult.warnings?.length > 0 && (
              <div className="mb-2">
                <p className="text-xs font-medium text-amber-700 mb-1">Advertencias:</p>
                {preflightResult.warnings.map((w, i) => <div key={i} className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded mb-1">{w}</div>)}
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-[#334155] mb-1">Puntos revisados ({preflightResult.checks?.length || 0}):</p>
              {(preflightResult.checks || []).map((c, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs py-0.5">
                  {c.passed ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
                  <span className="text-[#334155]">{c.name}</span>
                  {c.detail && <span className="text-[#94A3B8] ml-1">({c.detail})</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {members && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="members-modal">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto p-4">
            <div className="flex items-start justify-between mb-2 gap-3">
              <div>
                <h3 className="text-sm font-semibold text-[#0F172A]">¿Quién entra y quién no en esta campaña?</h3>
                <p className="text-xs text-[#94A3B8]">
                  {membersSchedule ? campaignName(membersSchedule.campaign_id) : ''}
                </p>
              </div>
              <button onClick={() => setMembers(null)} data-testid="members-close-btn" className="text-[#94A3B8] hover:text-[#0F172A]">x</button>
            </div>

            <p className="flex items-start gap-1.5 text-xs text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-1.5 rounded mb-3">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#94A3B8]" />
              <span>Por privacidad no mostramos nombres ni teléfonos. Usa el motivo de exclusión para corregir tu lista: por ejemplo, pide permiso a quien no lo tiene registrado.</span>
            </p>

            <div className="flex flex-wrap gap-1.5 mb-3">
              {MEMBER_FILTERS.map(f => (
                <button key={f.key || 'all'} onClick={() => fetchMembers(members.scheduleId, f.key, 1)}
                  data-testid={`members-filter-${f.key || 'all'}`}
                  className={`px-2 py-1 rounded-full text-xs border transition-colors ${members.filter === f.key ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-[#64748B] border-[#CBD5E1] hover:bg-[#F1F5F9]'}`}>
                  {f.label}
                </button>
              ))}
            </div>

            {members.loading && <p className="text-xs text-[#94A3B8]" data-testid="members-loading">Cargando contactos...</p>}
            {!members.loading && members.error && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-1.5 rounded" data-testid="members-error">{members.error}</p>
            )}
            {!members.loading && !members.error && members.items.length === 0 && (
              <p className="text-xs text-[#94A3B8]" data-testid="members-empty">Sin registros todavía para este filtro.</p>
            )}

            {!members.loading && !members.error && members.items.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="members-table">
                  <thead>
                    <tr className="text-left text-[#94A3B8] border-b border-[#E2E8F0]">
                      <th className="py-1.5 pr-2 font-medium">Contacto</th>
                      <th className="py-1.5 pr-2 font-medium">Canal</th>
                      <th className="py-1.5 pr-2 font-medium">Permiso</th>
                      <th className="py-1.5 pr-2 font-medium">¿Entra al envío?</th>
                      <th className="py-1.5 font-medium">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.items.map(m => (
                      <tr key={m.id} className="border-b border-[#F1F5F9]" data-testid={`member-row-${m.id}`}>
                        <td className="py-1.5 pr-2 text-[#64748B] font-mono">{String(m.contact_id || '').slice(0, 8)}</td>
                        <td className="py-1.5 pr-2 text-[#334155]">{m.channel || '—'}</td>
                        <td className="py-1.5 pr-2 text-[#334155]">{CONSENT_LABELS[m.consent_status] || m.consent_status || '—'}</td>
                        <td className={`py-1.5 pr-2 font-medium ${ELIGIBILITY_COLORS[m.eligibility_status] || 'text-[#334155]'}`}>
                          {ELIGIBILITY_LABELS[m.eligibility_status] || m.eligibility_status || '—'}
                        </td>
                        <td className="py-1.5 text-[#64748B]">{m.exclusion_reason ? (EXCLUSION_LABELS[m.exclusion_reason] || m.exclusion_reason) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!members.loading && !members.error && members.total > 0 && (
              <div className="flex items-center justify-between mt-3 text-xs text-[#64748B]">
                <span data-testid="members-count">
                  Mostrando {(members.page - 1) * MEMBERS_PAGE_SIZE + 1}–{Math.min(members.page * MEMBERS_PAGE_SIZE, members.total)} de {members.total} contactos
                </span>
                <div className="flex gap-1.5">
                  <button disabled={members.page <= 1} data-testid="members-prev-btn"
                    onClick={() => fetchMembers(members.scheduleId, members.filter, members.page - 1)}
                    className="px-2 py-1 border border-[#CBD5E1] rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F1F5F9]">Anterior</button>
                  <button disabled={members.page >= membersPages} data-testid="members-next-btn"
                    onClick={() => fetchMembers(members.scheduleId, members.filter, members.page + 1)}
                    className="px-2 py-1 border border-[#CBD5E1] rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F1F5F9]">Siguiente</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
